// FormsReport.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Select,
  Button,
  useTheme,
  useColorMode,
  Tag,
  Link,
  TagLabel,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faArrowDown,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cqAPI } from "../../lib/Auth";

function FormsReport() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({ key: "created", direction: "descending" });
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);

  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const fetchForms = async (params = {}) => {
    const { startDate, endDate } = params;
    setIsLoading(true);
    try {
      const formattedStartDate = startDate ? moment(startDate).format("L") : moment(dateRange[0]).format("L");
      const formattedEndDate = endDate ? moment(endDate).format("L") : moment(dateRange[1]).format("L");
      const resp = await cqAPI.get(`/reports/forms?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`);
      if (resp.type === "ok") {
        setRows(resp.data || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      console.error("Error fetching forms:", e);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Init from hash on mount (shareable)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const sd = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const ed = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const sr = hashParams.get("selectedRange") || "Today";

    setDateRange([sd, ed]);
    setSelectedRange(sr);
    fetchForms({ startDate: sd, endDate: ed });
  }, []);

  // Keep URL hash in sync
  useEffect(() => {
    const q = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = q;
  }, [startDate, endDate, selectedRange]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    const data = [...rows];
    if (!sortConfig.key) return data;

    const { key, direction } = sortConfig;
    const dir = direction === "ascending" ? 1 : -1;

    return data.sort((a, b) => {
      const va =
        key === "created" || key === "updated"
          ? (a[key] ? new Date(a[key]).getTime() : 0)
          : (a[key] ?? "").toString().toLowerCase();
      const vb =
        key === "created" || key === "updated"
          ? (b[key] ? new Date(b[key]).getTime() : 0)
          : (b[key] ?? "").toString().toLowerCase();

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rows, sortConfig]);
  const showLocationColumn = sortedRows.some((row) => row.location_name);
  const sentAtBasis = showLocationColumn ? "16%" : "18%";
  const patientBasis = showLocationColumn ? "18%" : "20%";
  const typeBasis = showLocationColumn ? "14%" : "15%";
  const titleBasis = showLocationColumn ? "22%" : "27%";
  const statusBasis = showLocationColumn ? "16%" : "20%";

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let s, e;
    switch (value) {
      case "Today":
        s = moment().startOf("day");
        e = moment().endOf("day");
        break;
      case "Yesterday":
        s = moment().subtract(1, "days").startOf("day");
        e = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        s = moment().subtract(6, "days").startOf("day");
        e = moment().endOf("day");
        break;
      case "Last 4 weeks":
        s = moment().subtract(4, "weeks").startOf("day");
        e = moment().endOf("day");
        break;
      case "Month to Date":
        s = moment().startOf("month");
        e = moment().endOf("day");
        break;
      case "Quarter to date":
        s = moment().startOf("quarter");
        e = moment().endOf("day");
        break;
      case "Year to date":
        s = moment().startOf("year");
        e = moment().endOf("day");
        break;
      case "Custom":
      default:
        s = null;
        e = null;
    }
    setDateRange([s ? s.toDate() : null, e ? e.toDate() : null]);
  };

  const renderCustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", { month: "long", year: "numeric" })}
        </Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return (
      <Box as="span" ml={2}>
        <FontAwesomeIcon icon={sortConfig.direction === "ascending" ? faArrowUp : faArrowDown} />
      </Box>
    );
  };

  const formatType = (t) => (t === "history" ? "History Request" : "Consent/Form");

  const statusFor = (row) => {
    if (row.updated) {
      const when = moment(row.updated).format("MMM D, YYYY h:mm a");
      return { label: `Completed • ${when}`, colorScheme: "green" };
    }
    return { label: "Pending", colorScheme: "yellow" };
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Controls */}
      <Flex mb={4} align="center">
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          width="200px"
          mr={2}
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        <Box ml="auto">
          <Button leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />} onClick={fetchForms}>
            Show Forms
          </Button>
        </Box>
      </Flex>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      {/* Table */}
      <VStack spacing={0} align="stretch">
        {/* Header */}
        <Flex borderBottom="1px" borderColor="gray.200" p={2} fontWeight="bold">
          <HStack spacing={8} flex="1">
            <Text flexBasis={sentAtBasis} cursor="pointer" onClick={() => requestSort("created")}>
              Sent At {renderSortIcon("created")}
            </Text>
            {showLocationColumn && <Text flexBasis="14%">Location</Text>}
            <Text flexBasis={patientBasis} cursor="pointer" onClick={() => requestSort("name")}>
              Patient {renderSortIcon("name")}
            </Text>
            <Text flexBasis={typeBasis} cursor="pointer" onClick={() => requestSort("type")}>
              Request Type {renderSortIcon("type")}
            </Text>
            <Text flexBasis={titleBasis} cursor="pointer" onClick={() => requestSort("title")}>
              Title {renderSortIcon("title")}
            </Text>
            <Text flexBasis={statusBasis} cursor="pointer" onClick={() => requestSort("updated")}>
              Status / Last Update {renderSortIcon("updated")}
            </Text>
          </HStack>
        </Flex>

        {/* Rows */}
        {sortedRows.map((row, idx) => {
          const status = statusFor(row);
          return (
            <Flex
              key={`${row.uid}-${row.created}-${idx}`}
              p={2}
              _hover={{ backgroundColor: hoverBgColor }}
            >
              <HStack spacing={8} flex="1" align="center">
                <Text flexBasis={sentAtBasis} textAlign="left">
                  {moment(row.created).format("MMM D, YYYY h:mm a")}
                </Text>
              {showLocationColumn && (
                <Text flexBasis="14%" textAlign="left">
                  {row.location_name || "-"}
                </Text>
              )}
              <Text flexBasis={patientBasis} textAlign="left">
                <Link
                  href={`/${row.type === "history" ? "history" : "forms"}?_uid=${row.uid}&bid=${row.bid}`}
                  isExternal
                  color="blue.500"
                  fontWeight="medium"
                >
                  <HStack spacing={1}>
                    <span>{row.name}</span>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                  </HStack>
                </Link>
              </Text>
                
                <Text flexBasis={typeBasis} textAlign="left">
                  {formatType(row.type)}
                </Text>
                <Text flexBasis={titleBasis} textAlign="left" noOfLines={2}>
                  {row.title}
                </Text>
                <Box flexBasis={statusBasis} textAlign="left">
                  <Tag size="md" variant="subtle" colorScheme={status.colorScheme}>
                    <TagLabel>{status.label}</TagLabel>
                  </Tag>
                </Box>
              </HStack>
            </Flex>
          );
        })}
        
      </VStack>
    </Box>
  );
}

export default FormsReport;
